import { type HelpSection, searchHelp, t, useAuth } from "@keel/core";
import { Empty, Markdown, SearchInput, Skeleton, useCrumbs } from "@keel/ui";
import { useEffect, useState } from "react";
import { Link, NavLink, useParams } from "react-router-dom";
import { articleHref, useHelp } from "../help";

/** The knowledge base (ERP-66): every article of the modules this account has, searchable. */
export function HelpPage() {
  const { module, slug } = useParams();
  const sections = useHelp();
  const [q, setQ] = useState("");
  // Opening an article (from a result or the list) leaves the search.
  // biome-ignore lint/correctness/useExhaustiveDependencies: on navigation only
  useEffect(() => setQ(""), [module, slug]);
  const section = sections?.find((s) => s.module === module);
  const article = section?.articles.find((a) => a.slug === slug);
  useCrumbs(
    article
      ? [{ label: t("Help"), to: "/help" }, { label: section!.title, to: "/help" }, { label: article.title }]
      : [{ label: t("Help") }],
  );
  if (!sections) return <Skeleton />;
  return (
    <div className="page">
      <div className="page-head">
        <div>
          <h1>{t("Help")}</h1>
          <p className="muted" style={{ margin: "4px 0 0" }}>
            {t(
              "How Keel works, page by page. The ? in the top bar opens the article for the page you're on.",
            )}
          </p>
        </div>
        <div style={{ width: "min(360px, 100%)" }}>
          <SearchInput value={q} onChange={setQ} placeholder={t("Search help…")} />
        </div>
      </div>
      <div className="help-layout">
        <HelpNav sections={sections} />
        <div>
          {q.trim() ? (
            <Results sections={sections} q={q} />
          ) : article ? (
            <div className="stack" style={{ gap: 10 }}>
              <Link to="/help" className="muted" style={{ fontSize: 13 }}>
                ← {t("All help articles")}
              </Link>
              <div className="card card-pad">
                <Markdown source={article.body} />
              </div>
            </div>
          ) : module || slug ? (
            <Empty
              title={t("No such article")}
              text={t("It may have moved. Search for it instead.")}
              icon="help"
            />
          ) : (
            <Overview sections={sections} />
          )}
        </div>
      </div>
    </div>
  );
}

function HelpNav({ sections }: { sections: HelpSection[] }) {
  return (
    <nav className="help-nav" aria-label={t("Help articles")}>
      {sections.map((s) => (
        <div key={s.module}>
          <h4>{s.title}</h4>
          {s.articles.map((a) => (
            <NavLink key={a.slug} to={articleHref(a)}>
              {a.title}
            </NavLink>
          ))}
        </div>
      ))}
      <div>
        <h4>{t("Keel")}</h4>
        <NavLink to="/whats-new">{t("What's new")}</NavLink>
      </div>
    </nav>
  );
}

function Results({ sections, q }: { sections: HelpSection[]; q: string }) {
  const hits = searchHelp(sections, q);
  if (!hits.length)
    return <Empty title={t("Nothing found")} text={t("Try fewer or other words.")} icon="search" />;
  return (
    <div className="card">
      {hits.map((h) => (
        <Link key={articleHref(h.article)} to={articleHref(h.article)} className="help-hit">
          <b>{h.article.title}</b> <span className="muted">· {h.section}</span>
          <small>{h.excerpt}</small>
        </Link>
      ))}
    </div>
  );
}

function Overview({ sections }: { sections: HelpSection[] }) {
  const { me } = useAuth();
  return (
    <div className="stack" style={{ gap: 26 }}>
      {sections.map((s) => (
        <section key={s.module}>
          <h3 style={{ margin: "0 0 10px" }}>{s.title}</h3>
          <div className="help-cards">
            {s.articles.map((a) => (
              <Link key={a.slug} to={articleHref(a)} className="card help-card">
                <b>{a.title}</b>
                <small>{a.summary}</small>
              </Link>
            ))}
          </div>
        </section>
      ))}
      {sections.length <= 1 && (me.modules ?? []).length > 0 && (
        <p className="muted">{t("The modules this account has don't have articles yet.")}</p>
      )}
    </div>
  );
}
